<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { base64ToFile } from "@/common/utils/files";
import { toast } from "@/common/utils/toast";

import {
  laboratoryList,
  laboratoryCreate,
  laboratoryDelete,
  laboratoryUpload,
  laboratoryModelCSV,
} from "@/common/api/rest/laboratory";

import type {
  LaboratoryListType,
  LaboratoryPayloadType,
} from "@/common/types/laboratory";
import type { FileType } from "@/common/types/common";

import { RegisterTheme } from "@/common/themes";
import { laboratoryForm, fileUploadForm } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const id = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "logo", label: null },
  { key: "name", label: t("common.labels.name") },
  { key: "document_name", label: t("common.labels.document") },
  { key: "document_number", label: t("common.labels.number") },
]);

const tableData = ref<LaboratoryListType[]>([]);

const selected = ref<LaboratoryListType | null>(null);
const payload = ref<LaboratoryPayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const isEdit = ref(false);
const showModal = ref(false);
const showConfirmDel = ref(false);
const showModalUpload = ref(false);

function fetchData() {
  laboratoryList(id.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);

  laboratoryCreate(id.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        router.push(
          `/${id.value}/register/laboratory/edit/${data.laboratory_id}`,
        );
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: LaboratoryListType) {
  router.push(`/${id.value}/register/laboratory/edit/${data.laboratory_id}`);
}

function remove(data: LaboratoryListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showConfirmDel.value = false;

  laboratoryDelete(id.value, selected.value.laboratory_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function closeModal() {
  payload.value = null;
  showModal.value = false;
  isEdit.value = false;
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  store.commit("SET_LOADING", true);
  showModalUpload.value = false;

  laboratoryUpload(id.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  laboratoryModelCSV(id.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watchEffect(() => {
  if (id.value) fetchData();
});
</script>

<template>
  <register-theme tab-active="laboratory">
    <template #title>
      <svg-icon icon="laboratory" size="2rem" />
      {{ t("common.labels.laboratories") }}
    </template>

    <template #content>
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        :upload="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:edit="edit"
        @btn:del="remove"
        @btn:upload="uploadCsv()"
      >
        <template #logo="{ data }">
          <base-avatar
            :url="data.avatar?.small"
            size="1.75rem"
            background="#ffffff"
          />
        </template>
      </baseTable>
    </template>
  </register-theme>

  <base-confirm
    :show="showConfirmDel"
    :title="
      t('common.labels.remove', {
        concat: t('common.labels.laboratory'),
      }).toUpperCase()
    "
    :message="
      t('common.texts.remove_message', {
        concat: selected?.name,
      })
    "
    @cancel="removeCancel"
    @confirm="removeConfirm"
  />

  <base-modal
    :show="showModal"
    :title="
      t('common.labels.add', {
        concat: t('common.labels.laboratory'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.add')"
    :button-close="t('common.labels.cancel')"
    @close="closeModal()"
    @confirm="addConfirm()"
  >
    <template #content>
      <laboratory-form :data="payload" @update-data="payload = $event" />
    </template>
  </base-modal>

  <base-modal
    :show="showModalUpload"
    :title="
      t('common.labels.upload', {
        concat: t('common.labels.company'),
      }).toUpperCase()
    "
    :show-button-confirm="true"
    :button-confirm="t('common.labels.send')"
    :button-close="t('common.labels.cancel')"
    @close="uploadCancel()"
    @confirm="uploadConfirm()"
  >
    <template #content>
      <file-upload-form
        :extensions="fileExtensions"
        :model="true"
        @update-data="fileUpload = $event"
        @model-download="downloadModel()"
      />
    </template>
  </base-modal>
</template>
